import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import { STATUS_COLORS, isManualDocument, progressLabel } from "./knowledgeHelpers";
import ManualKnowledgeDialog from "./ManualKnowledgeDialog";

const POLL_MS = 4000;

const useStyles = makeStyles()(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  table: {
    tableLayout: "fixed"
  },
  progressText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  statusLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  percent: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary
  },
  subText: {
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  reviewLine: {
    fontSize: 13,
    whiteSpace: "nowrap"
  },
  alertText: {
    fontSize: 12,
    color: theme.palette.warning.main,
    whiteSpace: "nowrap"
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover [data-chevron]": { color: theme.palette.primary.main }
  },
  chevron: {
    color: theme.palette.text.disabled,
    transition: "color .15s ease"
  },
  rowActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5)
  },
  fileButton: {
    justifyContent: "flex-start",
    textTransform: "none"
  },
  dialogHint: {
    fontSize: 12,
    color: theme.palette.text.secondary
  }
}));

const formatCost = usage =>
  usage?.costUsd ? `$${Number(usage.costUsd).toFixed(2)}` : "—";

const formatDate = value =>
  value
    ? new Date(value).toLocaleString(
        i18n.language?.startsWith("en") ? "en-US" : "es-EC",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        }
      )
    : "";

const progressPercent = progress =>
  progress?.step === "extracting" && progress.total
    ? Math.round((progress.done / progress.total) * 100)
    : null;

const EMPTY_FORM = { name: "", file: null };

const Knowledge = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const fileInputRef = useRef(null);

  const loadDocuments = useCallback(async () => {
    try {
      const { data } = await api.get("/knowledge");
      setDocuments(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const processing = documents.some(
    document => document.status === "processing"
  );

  useEffect(() => {
    if (!processing) return undefined;
    const timer = setInterval(loadDocuments, POLL_MS);
    return () => clearInterval(timer);
  }, [processing, loadDocuments]);

  const openDialog = () => {
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const upload = async () => {
    if (!form.file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", form.file);
      if (form.name.trim()) body.append("name", form.name.trim());
      const { data } = await api.post("/knowledge", body);
      toast.success(i18n.t("knowledge.uploaded"));
      setDialogOpen(false);
      navigate(`/knowledge/${data.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/knowledge/${toDelete.id}`);
      toast.success(i18n.t("knowledge.deleted"));
      loadDocuments();
    } catch (err) {
      toastError(err);
    } finally {
      setToDelete(null);
    }
  };

  return (
    <MainContainer maxWidth={false}>
      <ConfirmationModal
        title={i18n.t("knowledge.deleteTitle", { name: toDelete?.name || "" })}
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        onConfirm={remove}
      >
        {i18n.t("knowledge.deleteMessage")}
      </ConfirmationModal>

      <Dialog
        open={dialogOpen}
        onClose={() => !uploading && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{i18n.t("knowledge.uploadTitle")}</DialogTitle>
        <DialogContent
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingTop: 8
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            hidden
            onChange={e =>
              setForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))
            }
          />
          <Button
            variant="outlined"
            className={classes.fileButton}
            startIcon={<UploadFileOutlinedIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            {form.file ? form.file.name : i18n.t("knowledge.chooseFile")}
          </Button>
          <TextField
            label={i18n.t("knowledge.name")}
            placeholder={i18n.t("knowledge.namePlaceholder")}
            value={form.name}
            onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
            fullWidth
            size="small"
          />
          <Typography className={classes.dialogHint}>
            {i18n.t("knowledge.uploadHint")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={uploading}>
            {i18n.t("knowledge.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={upload}
            disabled={uploading || !form.file}
          >
            {uploading
              ? i18n.t("knowledge.uploading")
              : i18n.t("knowledge.upload")}
          </Button>
        </DialogActions>
      </Dialog>

      <ManualKnowledgeDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSaved={document => {
          toast.success(i18n.t("knowledge.manual.saved"));
          setManualOpen(false);
          navigate(`/knowledge/${document.id}`);
        }}
      />

      <MainHeader>
        <Title>{i18n.t("knowledge.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditNoteOutlinedIcon />}
            onClick={() => setManualOpen(true)}
          >
            {i18n.t("knowledge.manual.button")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={openDialog}
          >
            {i18n.t("knowledge.uploadButton")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("knowledge.columns.name")}</TableCell>
              <TableCell width={280}>
                {i18n.t("knowledge.columns.status")}
              </TableCell>
              <TableCell width={200}>
                {i18n.t("knowledge.columns.review")}
              </TableCell>
              <TableCell width={80} align="right">
                {i18n.t("knowledge.columns.cost")}
              </TableCell>
              <TableCell width={160}>
                {i18n.t("knowledge.columns.date")}
              </TableCell>
              <TableCell width={90} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && documents.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {i18n.t("knowledge.empty")}
                </TableCell>
              </TableRow>
            )}
            {documents.map(document => {
              const percent = progressPercent(document.progress);
              const { total, approved = 0, withAlerts } = document.counts;
              return (
                <TableRow
                  key={document.id}
                  hover
                  className={classes.clickableRow}
                  onClick={() => navigate(`/knowledge/${document.id}`)}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      noWrap
                      fontWeight={600}
                      title={document.name}
                    >
                      {document.name} · v{document.version}
                    </Typography>
                    <Typography
                      className={classes.subText}
                      noWrap
                      title={document.originalName}
                    >
                      {isManualDocument(document)
                        ? i18n.t("knowledge.manual.source")
                        : [
                            document.originalName,
                            document.pageCount
                              ? i18n.t("knowledge.pagesCount", {
                                  count: document.pageCount
                                })
                              : null
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div className={classes.statusLine}>
                      <Chip
                        size="small"
                        color={STATUS_COLORS[document.status] || "default"}
                        variant="outlined"
                        label={i18n.t(
                          `knowledge.documentStatus.${document.status}`
                        )}
                      />
                      {percent !== null && (
                        <span className={classes.percent}>{percent}%</span>
                      )}
                    </div>
                    {document.status === "processing" && (
                      <>
                        <LinearProgress
                          variant={
                            percent !== null ? "determinate" : "indeterminate"
                          }
                          value={percent ?? 0}
                          style={{ marginTop: 6, borderRadius: 4 }}
                        />
                        <Typography
                          className={classes.progressText}
                          title={progressLabel(document.progress)}
                        >
                          {progressLabel(document.progress)}
                        </Typography>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {total ? (
                      <>
                        <div className={classes.reviewLine}>
                          {i18n.t("knowledge.reviewProgress", {
                            approved,
                            total
                          })}
                        </div>
                        <LinearProgress
                          variant="determinate"
                          color="success"
                          value={(approved / total) * 100}
                          style={{ marginTop: 6, borderRadius: 4 }}
                        />
                        {withAlerts > 0 && (
                          <div className={classes.alertText}>
                            {i18n.t("knowledge.summary.withAlerts", {
                              count: withAlerts
                            })}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className={classes.subText}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {formatCost(document.usage)}
                  </TableCell>
                  <TableCell>
                    <span className={classes.reviewLine}>
                      {formatDate(document.createdAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className={classes.rowActions}>
                      <Tooltip title={i18n.t("knowledge.delete")}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setToDelete(document);
                            }}
                            disabled={document.status === "processing"}
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <ChevronRightIcon className={classes.chevron} data-chevron="" />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Knowledge;
